import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import VendorRequestHistoryApiService from "@/core/services/api.service.vendor-request-history";

export default {
  props: {},
  name: "vendor-request-history",
  components: {},
  data() {
    return {
      pagination: {
        page: 1,
        itemsPerPage: 10
      },
      records: [],
      loading: false,
      componentLoaded: false,
      hasNext: false,
      // Filters
      statusFilter: "",
      vendorNameFilter: "",
      eventTypeFilter: "",
      userEmailFilter: "",
      errorCodeFilter: "",
      dateFromFilter: null,
      dateToFilter: null,
      dateFromMenu: false,
      dateToMenu: false,
      // Filter options
      statusOptions: [{ text: "All", value: "" }],
      // Stats
      stats: null,
      statsLoading: false,
      showStats: true,
      statsTab: 0,
      // Chart controls
      chartType: "bar",
      chartTypeOptions: [
        { text: "Bar (stacked)", value: "bar" },
        { text: "Pie", value: "pie" }
      ],
      selectedVendors: [],
      selectedStatuses: [],
      // Detail dialog
      detailDialog: false,
      selectedRecord: null,
      // Table headers
      headers: [
        { text: "ID", value: "id", sortable: false, width: "80px" },
        { text: "User Email", value: "userEmail", sortable: false },
        { text: "Vendor", value: "vendorName", sortable: false },
        { text: "Event Type", value: "eventType", sortable: false },
        { text: "Status", value: "status", sortable: false },
        {
          text: "Status Code",
          value: "statusCode",
          sortable: false,
          width: "100px",
          align: "center"
        },
        { text: "Error Code", value: "errorCode", sortable: false },
        { text: "Retries", value: "retryCount", sortable: false, width: "80px", align: "center" },
        { text: "Created At", value: "createdAt", sortable: false },
        { text: "Last Attempt", value: "lastAttemptAt", sortable: false }
      ]
    };
  },
  watch: {
    "pagination.page"() {
      if (!this.componentLoaded) return;
      this.fetchRecords();
    }
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Vendor Request History" }]);
    await VendorRequestHistoryApiService.init();
    this.componentLoaded = true;
    this.loadStatuses();
    this.loadStats();
    this.fetchRecords();
  },
  methods: {
    async loadStatuses() {
      try {
        const response = await VendorRequestHistoryApiService.getStatuses();
        if (response?.data) {
          this.statusOptions = [
            { text: "All", value: "" },
            ...response.data.map(s => ({ text: s, value: s }))
          ];
        }
      } catch (error) {
        console.error("Error loading statuses:", error);
      }
    },

    async loadStats() {
      this.statsLoading = true;
      try {
        const params = {};
        if (this.dateFromFilter) params.from = this.dateFromFilter + "T00:00:00";
        if (this.dateToFilter) params.to = this.dateToFilter + "T23:59:59";

        const response = await VendorRequestHistoryApiService.getStats(params);
        if (response?.data) {
          this.stats = response.data;
        }
      } catch (error) {
        console.error("Error loading stats:", error);
      } finally {
        this.statsLoading = false;
      }
    },

    async fetchRecords() {
      if (this.loading) return;

      const params = {
        page: this.pagination.page - 1,
        size: this.pagination.itemsPerPage
      };

      if (this.statusFilter) params.status = this.statusFilter;
      if (this.vendorNameFilter) params.vendorName = this.vendorNameFilter.trim();
      if (this.eventTypeFilter) params.eventType = this.eventTypeFilter.trim();
      if (this.userEmailFilter) params.userEmail = this.userEmailFilter.trim();
      if (this.errorCodeFilter) params.errorCode = this.errorCodeFilter.trim();
      if (this.dateFromFilter) params.from = this.dateFromFilter + "T00:00:00";
      if (this.dateToFilter) params.to = this.dateToFilter + "T23:59:59";

      this.loading = true;
      try {
        const response = await VendorRequestHistoryApiService.getRecords(params);
        if (response?.data) {
          this.records = response.data.data || [];
          this.hasNext = response.data.hasNext || false;
        }
      } catch (error) {
        console.error("Error loading records:", error);
        this.showToast("Failed to load vendor request history", "danger");
      } finally {
        this.loading = false;
      }
    },

    onPageSizeChange(val) {
      this.pagination.itemsPerPage = Number(val);
      this.pagination.page = 1;
      this.fetchRecords();
    },

    applyFilters() {
      this.pagination.page = 1;
      this.fetchRecords();
      this.loadStats();
    },

    resetFilters() {
      this.statusFilter = "";
      this.vendorNameFilter = "";
      this.eventTypeFilter = "";
      this.userEmailFilter = "";
      this.errorCodeFilter = "";
      this.dateFromFilter = null;
      this.dateToFilter = null;
      this.pagination.page = 1;
      this.fetchRecords();
      this.loadStats();
    },

    clearFilter(field) {
      this[field] = field.includes("date") ? null : "";
      this.pagination.page = 1;
      this.fetchRecords();
    },

    openDetail(item) {
      this.selectedRecord = item;
      this.detailDialog = true;
    },

    closeDetail() {
      this.detailDialog = false;
      this.selectedRecord = null;
    },

    nextPage() {
      if (this.hasNext) {
        this.pagination.page++;
      }
    },

    prevPage() {
      if (this.pagination.page > 1) {
        this.pagination.page--;
      }
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },

    getStatusColor(status) {
      const colors = {
        PENDING: "orange",
        IN_PROGRESS: "blue",
        SUCCESS: "green",
        FAILED: "red"
      };
      return colors[status] || "grey";
    },

    getStatusHex(status) {
      const colors = {
        PENDING: "#FF9800",
        IN_PROGRESS: "#2196F3",
        SUCCESS: "#4CAF50",
        FAILED: "#F44336"
      };
      return colors[status] || "#9E9E9E";
    },

    formatJson(obj) {
      if (!obj) return "-";
      try {
        return JSON.stringify(obj, null, 2);
      } catch {
        return String(obj);
      }
    },

    showToast(message, variant) {
      this.$bvToast?.toast(message, {
        variant: variant,
        solid: true,
        autoHideDelay: 4000
      });
    }
  },
  computed: {
    name() {
      return "Vendor Request History";
    },
    canGoPrev() {
      return this.pagination.page > 1;
    },
    canGoNext() {
      return this.hasNext;
    },
    paginationInfo() {
      const from = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1;
      const to = from + this.records.length - 1;
      return `${from} - ${to}`;
    },
    vendorNames() {
      if (!this.stats?.byVendor) return [];
      return Object.keys(this.stats.byVendor).sort();
    },
    allStatuses() {
      return ["PENDING", "IN_PROGRESS", "SUCCESS", "FAILED"];
    },
    filteredVendors() {
      return this.selectedVendors.filter(v => this.stats?.byVendor?.[v]);
    },
    filteredStatuses() {
      return this.selectedStatuses.filter(s => this.allStatuses.includes(s));
    },
    isBarChart() {
      return this.chartType === "bar";
    },
    vendorStatusChartOptions() {
      if (this.isBarChart) {
        return {
          chart: { type: "bar", stacked: true, toolbar: { show: true } },
          plotOptions: {
            bar: { horizontal: false, columnWidth: "50%" }
          },
          xaxis: { categories: this.filteredVendors },
          colors: this.filteredStatuses.map(s => this.getStatusHex(s)),
          legend: { position: "bottom" },
          title: { text: "Requests by Vendor & Status", align: "center" },
          dataLabels: { enabled: true },
          tooltip: {
            y: { formatter: val => val + " requests" }
          }
        };
      }
      // Pie chart — labels are "vendor - status" combos
      const labels = [];
      const colors = [];
      this.filteredVendors.forEach(vendor => {
        this.filteredStatuses.forEach(status => {
          const count = this.stats?.byVendor?.[vendor]?.[status] || 0;
          if (count > 0) {
            labels.push(`${vendor} — ${status}`);
            colors.push(this.getStatusHex(status));
          }
        });
      });
      return {
        chart: { type: "pie" },
        labels: labels,
        colors: colors,
        legend: { position: "bottom" },
        title: { text: "Requests by Vendor & Status", align: "center" }
      };
    },
    vendorStatusChartSeries() {
      if (
        !this.stats?.byVendor ||
        this.filteredVendors.length === 0 ||
        this.filteredStatuses.length === 0
      )
        return [];
      if (this.isBarChart) {
        return this.filteredStatuses.map(status => ({
          name: status,
          data: this.filteredVendors.map(vendor => this.stats.byVendor[vendor]?.[status] || 0)
        }));
      }
      // Pie chart — flat array of values
      const series = [];
      this.filteredVendors.forEach(vendor => {
        this.filteredStatuses.forEach(status => {
          const count = this.stats?.byVendor?.[vendor]?.[status] || 0;
          if (count > 0) {
            series.push(count);
          }
        });
      });
      return series;
    },
    hasChartData() {
      return this.vendorStatusChartSeries.length > 0;
    }
  }
};
